import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { formatErr } from "@/lib/api";
import { LogIn, ShieldCheck, KeyRound, Smartphone, Copy, Check, ArrowLeft } from "lucide-react";

const STEP_CRED = "cred";
const STEP_SETUP = "setup";
const STEP_VERIFY = "verify";

export default function Login() {
  const { user, finalizeSession } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState(STEP_CRED);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [tfaUserEmail, setTfaUserEmail] = useState("");
  const [tfaUserName, setTfaUserName] = useState("");
  const [setupData, setSetupData] = useState(null); // {secret, qr_image_data_url, otpauth_url}
  const [otpCode, setOtpCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const reset = () => {
    setStep(STEP_CRED);
    setTempToken(""); setOtpCode(""); setSetupData(null);
    setTfaUserEmail(""); setTfaUserName(""); setErr("");
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_setup) {
        setTempToken(data.temp_token);
        setTfaUserEmail(data.user_email);
        setTfaUserName(data.user_name);
        // Pre-fetch setup
        const r = await api.post("/auth/2fa/setup-pending", { temp_token: data.temp_token });
        setSetupData(r.data);
        setStep(STEP_SETUP);
      } else if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setTfaUserEmail(data.user_email);
        setTfaUserName(data.user_name);
        setStep(STEP_VERIFY);
      } else {
        // Direct login (admin)
        finalizeSession(data);
        nav("/");
      }
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const submitEnroll = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/2fa/enroll", { temp_token: tempToken, code: otpCode });
      finalizeSession(data);
      nav("/");
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/2fa/login", { temp_token: tempToken, code: otpCode });
      finalizeSession(data);
      nav("/");
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!setupData?.secret) return;
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  };

  return (
    <div className="min-h-screen w-full bg-bg flex items-center justify-center px-4 relative overflow-hidden" data-testid="login-page">
      {/* Decorative background */}
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-cyan/10 blur-[120px]" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-violet-500/10 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full border border-cyan/10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-cyan/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[980px] h-[980px] rounded-full border border-cyan/[0.03]" />
      </div>

      {/* Top brand */}
      <div className="absolute top-6 left-6 flex items-center gap-2.5 z-10">
        <img src="/assets/orbit-logo.svg" alt="ORBIT" className="w-10 h-10 object-contain" data-testid="orbit-logo-login" />
        <div>
          <div className="font-bold tracking-tight text-base">ORBIT</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">CS Console</div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.25em] text-slate-600 flex items-center gap-2 z-10">
        <ShieldCheck className="w-3 h-3" />
        Authorized Personnel Only · Jakarta Operations
      </div>

      {step === STEP_CRED && (
        <form onSubmit={submitCreds} className="relative z-10 w-full max-w-md bg-surface/80 backdrop-blur-xl border border-line rounded-sm p-8 space-y-5 shadow-2xl animate-slide-up" data-testid="login-form">
          <div className="space-y-2 text-center">
            <div className="text-[10px] uppercase tracking-[0.35em] text-cyan">Payment Ops · Mission Control</div>
            <h1 className="text-3xl font-bold tracking-tight">Welcome back, operator.</h1>
            <p className="text-xs text-slate-400">Masuk dengan kredensial yang diberikan admin.</p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="w-full bg-bg border border-line rounded-sm px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan/40 transition"
                placeholder="you@orbit.id"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-bg border border-line rounded-sm px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan/40 transition"
                placeholder="••••••••"
              />
            </div>
          </div>

          {err && (
            <div data-testid="login-error" className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">
              {err}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full bg-cyan hover:bg-cyan-dark text-bg font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-60"
          >
            <LogIn className="w-4 h-4" />
            {busy ? "Signing in..." : "Sign in to console"}
          </button>

          <div className="text-[10px] text-slate-500 border-t border-line pt-4 flex items-start gap-2">
            <ShieldCheck className="w-3 h-3 mt-0.5 text-cyan flex-shrink-0" />
            <div className="text-slate-400 leading-relaxed">
              <span className="text-slate-300 font-semibold">2FA wajib aktif</span> untuk Supervisor & Agent. Pada login pertama, Anda akan diminta scan QR code dengan Google Authenticator.
            </div>
          </div>
        </form>
      )}

      {step === STEP_SETUP && setupData && (
        <form onSubmit={submitEnroll} className="relative z-10 w-full max-w-md bg-surface/80 backdrop-blur-xl border border-line rounded-sm p-8 space-y-5 shadow-2xl animate-slide-up" data-testid="login-2fa-setup">
          <button type="button" onClick={reset} className="text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan flex items-center gap-1" data-testid="back-to-login">
            <ArrowLeft className="w-3 h-3" /> Kembali
          </button>
          <div className="space-y-2 text-center">
            <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400 flex items-center justify-center gap-1.5">
              <Smartphone className="w-3 h-3" /> Setup 2FA — Wajib
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Aktifkan Google Authenticator</h1>
            <p className="text-xs text-slate-400">Halo <span className="text-cyan">{tfaUserName || tfaUserEmail}</span>, scan QR code di bawah dengan aplikasi <strong>Google Authenticator</strong> / <strong>Authy</strong>.</p>
          </div>

          <div className="flex justify-center">
            <div className="bg-white p-3 rounded-sm" data-testid="2fa-qr-code">
              <img src={setupData.qr_image_data_url} alt="QR code" className="w-44 h-44 object-contain" />
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Atau input manual</div>
            <div className="flex items-center gap-2 bg-bg border border-line rounded-sm px-3 py-2">
              <code className="font-mono text-xs flex-1 text-slate-200 break-all" data-testid="2fa-secret">{setupData.secret}</code>
              <button type="button" onClick={copySecret} className="text-cyan hover:text-cyan-dark" data-testid="copy-secret-btn">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Masukkan 6 digit kode dari Authenticator</label>
            <input
              data-testid="2fa-enroll-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
              className="w-full bg-bg border border-line rounded-sm px-3 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan/40 transition"
              placeholder="123456"
            />
          </div>

          {err && (
            <div data-testid="login-error" className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">
              {err}
            </div>
          )}

          <button
            data-testid="enroll-submit-btn"
            type="submit"
            disabled={busy || otpCode.length !== 6}
            className="w-full bg-cyan hover:bg-cyan-dark text-bg font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-60"
          >
            <ShieldCheck className="w-4 h-4" />
            {busy ? "Mengaktifkan..." : "Aktifkan 2FA & Lanjutkan"}
          </button>
        </form>
      )}

      {step === STEP_VERIFY && (
        <form onSubmit={submitVerify} className="relative z-10 w-full max-w-md bg-surface/80 backdrop-blur-xl border border-line rounded-sm p-8 space-y-5 shadow-2xl animate-slide-up" data-testid="login-2fa-verify">
          <button type="button" onClick={reset} className="text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan flex items-center gap-1" data-testid="back-to-login">
            <ArrowLeft className="w-3 h-3" /> Kembali
          </button>
          <div className="space-y-2 text-center">
            <div className="text-[10px] uppercase tracking-[0.35em] text-cyan flex items-center justify-center gap-1.5">
              <KeyRound className="w-3 h-3" /> Verifikasi 2FA
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Masukkan kode Authenticator</h1>
            <p className="text-xs text-slate-400">Buka <strong>Google Authenticator</strong> dan masukkan kode 6 digit untuk <span className="text-cyan font-mono">{tfaUserEmail}</span>.</p>
          </div>

          <div>
            <input
              data-testid="2fa-verify-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
              className="w-full bg-bg border border-line rounded-sm px-3 py-4 text-center text-3xl font-mono tracking-[0.5em] focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan/40 transition"
              placeholder="••••••"
            />
          </div>

          {err && (
            <div data-testid="login-error" className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">
              {err}
            </div>
          )}

          <button
            data-testid="verify-submit-btn"
            type="submit"
            disabled={busy || otpCode.length !== 6}
            className="w-full bg-cyan hover:bg-cyan-dark text-bg font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-60"
          >
            <LogIn className="w-4 h-4" />
            {busy ? "Verifying..." : "Verifikasi & Login"}
          </button>

          <div className="text-[10px] text-slate-500 text-center border-t border-line pt-3">
            Tidak punya akses Authenticator? Hubungi admin untuk reset 2FA.
          </div>
        </form>
      )}
    </div>
  );
}
